import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  CheckCircle, 
  Truck, 
  Users, 
  MapPin, 
  Clock, 
  Shield, 
  BarChart3,
  Calendar,
  Bell,
  Navigation,
  Heart,
  Star,
  Zap,
  FileText,
  CreditCard,
  Headphones,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Award,
  TrendingUp,
  DollarSign,
  Building2,
  Car,
  Accessibility,
  CloudSun
} from "lucide-react";

export default function BusinessProposalPage() {
  const navigate = useNavigate();
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-pink-500/10"></div>
        <div className="max-w-7xl mx-auto px-6 py-20 relative">
          <div className="text-center">
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              <span className="bg-gradient-to-r from-cyan-400 via-white to-purple-400 bg-clip-text text-transparent">
                There You Are
              </span>
            </h1>
            <p className="text-xl text-orange-400 font-semibold mb-2">— Solutions —</p>
            <p className="text-2xl text-slate-300 mb-4">NEMT & Personal Transportation Services</p>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto mb-10">
              A comprehensive, cloud-based transportation management solution providing reliable medical transportation 
              and personal rides for everyday needs. Serving <span className="text-cyan-400 font-semibold">3.6 million Americans</span> who 
              miss medical appointments annually, plus everyday rides to work, shopping, church, and more.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <button 
                onClick={() => navigate('/login')}
                className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-400 hover:to-cyan-500 text-white px-8 py-4 rounded-xl font-semibold text-lg flex items-center gap-2 transition-all shadow-lg shadow-cyan-500/25"
              >
                Start 14-Day Free Trial
                <ArrowRight className="w-5 h-5" />
              </button>
              <button 
                onClick={() => navigate('/login')}
                className="bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-xl font-semibold text-lg border border-white/20 transition-all"
              >
                Try Our Demos
              </button>
              <button 
                onClick={() => navigate('/pricing')}
                className="bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-400 hover:to-rose-400 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all"
              >
                Pricing
              </button>
              <button 
                onClick={() => navigate('/login')}
                className="bg-slate-700/50 hover:bg-slate-700 text-white px-8 py-4 rounded-xl font-semibold text-lg border border-slate-600 transition-all"
              >
                Sign In
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Problem We Solve */}
      <section className="py-20 bg-slate-800/50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-white text-center mb-4">The Problem We Solve</h2>
          <p className="text-slate-400 text-center mb-12 max-w-2xl mx-auto">
            Healthcare transportation challenges cost the industry billions annually. We provide the solution.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { problem: "Missed Appointments", impact: "$150B annual healthcare cost", solution: "Real-time tracking & reminders", icon: Calendar },
              { problem: "Inefficient Routing", impact: "30% deadhead driving", solution: "Smart route optimization", icon: Navigation },
              { problem: "Poor Communication", impact: "Patient anxiety & no-shows", solution: "Live ETA sharing with family", icon: Bell },
              { problem: "Accessibility Gaps", impact: "Special needs underserved", solution: "Comprehensive accessibility tracking", icon: Accessibility },
              { problem: "Manual Scheduling", impact: "Hours of admin work", solution: "One-click & batch scheduling", icon: Clock },
              { problem: "Billing Complexity", impact: "Delayed reimbursements", solution: "Automated claim generation", icon: FileText },
            ].map((item, i) => (
              <div key={i} className="bg-slate-900/50 border border-slate-700 rounded-2xl p-6 hover:border-cyan-500/50 transition-all">
                <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-red-400" />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">{item.problem}</h3>
                <p className="text-red-400 text-sm mb-3">{item.impact}</p>
                <div className="flex items-center gap-2 text-green-400 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  {item.solution}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Three Portals */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-white text-center mb-4">Three Integrated Portals</h2>
          <p className="text-slate-400 text-center mb-12">One platform serving all stakeholders</p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                title: "Dispatch Center", 
                subtitle: "Fleet Management",
                icon: Building2,
                color: "from-blue-500 to-blue-600",
                features: ["Live Fleet Map", "Ride Progress Timeline", "One-Click Scheduling", "Driver Leaderboard", "Claim Generation"]
              },
              { 
                title: "Driver Dashboard", 
                subtitle: "Field Operations",
                icon: Car,
                color: "from-green-500 to-green-600",
                features: ["Interactive Route Map", "Ride Timeline", "Daily Schedule", "Accessibility Alerts", "Emergency Quick-Dial"]
              },
              { 
                title: "Client Portal", 
                subtitle: "Patient Experience",
                icon: Heart,
                color: "from-pink-500 to-pink-600",
                features: ["Live Driver Tracking", "Journey Timeline", "ETA Sharing", "Ride Sharing Options", "Ride History"]
              },
            ].map((portal, i) => (
              <div key={i} className="bg-gradient-to-b from-slate-800 to-slate-900 border border-slate-700 rounded-2xl overflow-hidden hover:border-slate-600 transition-all">
                <div className={`bg-gradient-to-r ${portal.color} p-6`}>
                  <portal.icon className="w-12 h-12 text-white mb-4" />
                  <h3 className="text-white font-bold text-xl">{portal.title}</h3>
                  <p className="text-white/80">{portal.subtitle}</p>
                </div>
                <div className="p-6">
                  <ul className="space-y-3">
                    {portal.features.map((feature, j) => (
                      <li key={j} className="flex items-center gap-3 text-slate-300">
                        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Personal Transportation Services - NEW */}
      <section className="py-20 bg-gradient-to-r from-orange-500/5 via-amber-500/5 to-orange-500/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-orange-500/20 text-orange-400 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <Car className="w-4 h-4" />
              Also Available
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Personal Transportation Services</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Beyond medical transportation, we provide dependable prearranged personal rides for everyday needs. 
              Whether you need a ride to work, the grocery store, church, or family visits.
            </p>
            <p className="text-orange-400 mt-2 italic">"Getting You There Matters"</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Services Offered */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <Car className="w-6 h-6 text-orange-400" />
                Services Offered
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { name: "One-Way Rides", icon: "→" },
                  { name: "Round-Trip Rides", icon: "↔" },
                  { name: "Grocery & Shopping", icon: "🛒" },
                  { name: "Work Transportation", icon: "💼" },
                  { name: "Church & Events", icon: "⛪" },
                  { name: "Errand Runs", icon: "📦" },
                  { name: "Multiple Stops", icon: "📍" },
                  { name: "Wait & Return", icon: "⏱" }
                ].map((service, i) => (
                  <div key={i} className="flex items-center gap-3 text-slate-300 bg-slate-700/30 rounded-lg p-3">
                    <span className="text-xl">{service.icon}</span>
                    <span>{service.name}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Starting Rates */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <DollarSign className="w-6 h-6 text-green-400" />
                Starting Rates
              </h3>
              <div className="space-y-3">
                {[
                  { service: "Base Fare / Pickup", price: "$5.00" },
                  { service: "Mileage", price: "$2.25/mile" },
                  { service: "Minimum One-Way Fare", price: "$15.00" },
                  { service: "Additional Stop", price: "$5.00" },
                  { service: "First 5 Min at Pickup", price: "Free" },
                  { service: "Wait & Return (30 min)", price: "$20.00" },
                  { service: "Wait & Return (60 min)", price: "$40.00" },
                  { service: "Each Add'l 30 Minutes", price: "$20.00" },
                  { service: "Late Cancellation", price: "$10.00" },
                  { service: "No-Show Fee", price: "$15.00" }
                ].map((item, i) => (
                  <div key={i} className="flex justify-between items-center text-slate-300 border-b border-slate-700/50 pb-2">
                    <span>{item.service}</span>
                    <span className="text-green-400 font-semibold">{item.price}</span>
                  </div>
                ))}
              </div>
              <p className="text-slate-500 text-xs mt-4 italic">
                *Rates may vary based on distance, trip type, time, and service area.
              </p>
            </div>
          </div>
          
          {/* Why Choose Us */}
          <div className="bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-500/30 rounded-2xl p-8">
            <h3 className="text-xl font-bold text-white mb-6 text-center">Why Choose There You Are Solutions?</h3>
            <div className="grid md:grid-cols-5 gap-4">
              {[
                "Scheduled rides you can count on",
                "Friendly, dependable service",
                "Ideal for personal transportation",
                "Great for seniors and families",
                "Convenient round-trip options"
              ].map((reason, i) => (
                <div key={i} className="flex items-center gap-2 text-slate-300 text-sm">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  {reason}
                </div>
              ))}
            </div>
          </div>
          
          {/* Contact Info */}
          <div className="mt-8 text-center">
            <p className="text-slate-400 mb-4">Serving <span className="text-orange-400 font-semibold">Dayton, Ohio</span> and surrounding communities</p>
            <div className="flex flex-wrap justify-center gap-6">
              <a href="tel:937-519-9189" className="flex items-center gap-2 text-white bg-orange-500/20 hover:bg-orange-500/30 px-6 py-3 rounded-xl transition-all">
                <span className="text-2xl">📞</span>
                <span className="font-semibold">937-519-9189</span>
              </a>
              <a href="mailto:contact@thereyouare.online" className="flex items-center gap-2 text-white bg-slate-700/50 hover:bg-slate-700 px-6 py-3 rounded-xl transition-all">
                <span className="text-2xl">✉️</span>
                <span>Thereyouare.online</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Accessibility Compliance */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-3xl p-8 md:p-12">
            <div className="flex items-center gap-3 mb-6">
              <Accessibility className="w-8 h-8 text-amber-400" />
              <h2 className="text-3xl font-bold text-white">Accessibility Compliance</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-amber-400 font-semibold mb-4">Mobility Aids Supported</h3>
                <ul className="space-y-2">
                  {["Wheelchair (manual & electric)", "Rolling Walker", "Cane", "Crutches", "Oxygen Equipment"].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-300">
                      <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-amber-400 font-semibold mb-4">Assistance Services Tracked</h3>
                <ul className="space-y-2">
                  {["Step Stool Required", "Entry/Exit Assistance", "Visually Impaired", "Hearing Impaired", "Service Animal"].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-300">
                      <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-slate-800/50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-white text-center mb-4">Transparent Pricing</h2>
          <p className="text-slate-400 text-center mb-12">Simple flat-rate pricing. No hidden fees.</p>
          
          <div className="flex justify-center">
            {[
              { 
                name: "Per Vehicle", 
                price: "$59", 
                fleet: "Any fleet size",
                popular: true
              },
            ].map((plan, i) => (
              <div 
                key={i} 
                className="relative bg-slate-900/50 border rounded-2xl p-8 border-cyan-500 shadow-lg shadow-cyan-500/20 max-w-md mx-auto"
              >
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-cyan-500 text-white text-sm font-semibold px-4 py-1 rounded-full">
                  Simple Pricing
                </div>
                <h3 className="text-white font-bold text-xl mb-2">{plan.name}</h3>
                <p className="text-slate-400 text-sm mb-6">{plan.fleet}</p>
                
                <div className="mb-8">
                  <span className="text-5xl font-bold text-white">{plan.price}</span>
                  <span className="text-slate-400">/vehicle/month</span>
                </div>
                
                <ul className="space-y-3 mb-8">
                  {["Unlimited rides", "All features included", "Real-time tracking", "Route optimization", "Claim generation", "Email support"].map((feature, j) => (
                    <li key={j} className="flex items-center gap-3 text-slate-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      {feature}
                    </li>
                  ))}
                </ul>
                
                <button 
                  onClick={() => navigate('/free-trial')}
                  className="w-full py-3 rounded-xl font-semibold transition-all bg-cyan-500 hover:bg-cyan-400 text-white"
                >
                  Start Free Trial
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI Calculator */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-white text-center mb-4">Return on Investment</h2>
          <p className="text-slate-400 text-center mb-12">Sample ROI for a 10-vehicle fleet</p>
          
          <div className="max-w-2xl mx-auto bg-gradient-to-b from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-2xl p-8">
            <div className="space-y-4">
              {[
                { label: "Fuel savings (25% reduction)", value: "$500", positive: true },
                { label: "Additional rides (20% more)", value: "$2,000", positive: true },
                { label: "Admin time savings (10 hrs × $25)", value: "$250", positive: true },
                { label: "Reduced no-shows", value: "$400", positive: true },
              ].map((item, i) => (
                <div key={i} className="flex justify-between items-center py-3 border-b border-green-500/20">
                  <span className="text-slate-300">{item.label}</span>
                  <span className="text-green-400 font-semibold">+{item.value}</span>
                </div>
              ))}
              
              <div className="flex justify-between items-center py-3 border-b border-green-500/20">
                <span className="text-white font-semibold">Total Monthly Benefit</span>
                <span className="text-green-400 font-bold text-xl">$3,150</span>
              </div>
              
              <div className="flex justify-between items-center py-3 border-b border-green-500/20">
                <span className="text-slate-300">Platform Cost (10 × $59)</span>
                <span className="text-red-400 font-semibold">-$590</span>
              </div>
              
              <div className="flex justify-between items-center py-4 bg-green-500/20 rounded-xl px-4 mt-4">
                <span className="text-white font-bold text-lg">Net Monthly ROI</span>
                <span className="text-green-400 font-bold text-2xl">$2,560</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology & Security */}
      <section className="py-20 bg-slate-800/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-white mb-8">Technology Stack</h2>
              <div className="space-y-4">
                {[
                  { tech: "React 18 + Tailwind CSS", benefit: "Modern, responsive UI" },
                  { tech: "FastAPI (Python)", benefit: "High-performance API" },
                  { tech: "MongoDB", benefit: "Flexible, scalable storage" },
                  { tech: "Stripe Payments", benefit: "PCI-compliant billing" },
                  { tech: "Google Maps & Waze", benefit: "Industry-standard navigation" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between bg-slate-900/50 border border-slate-700 rounded-xl p-4">
                    <span className="text-white font-medium">{item.tech}</span>
                    <span className="text-slate-400 text-sm">{item.benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h2 className="text-3xl font-bold text-white mb-8">Security & Compliance</h2>
              <div className="space-y-4">
                {[
                  { feature: "End-to-end encryption", icon: Shield },
                  { feature: "HIPAA-ready architecture", icon: Heart },
                  { feature: "PCI DSS compliant payments", icon: CreditCard },
                  { feature: "Role-based access control", icon: Users },
                  { feature: "99.9% uptime SLA", icon: Zap },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4 bg-slate-900/50 border border-slate-700 rounded-xl p-4">
                    <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-cyan-400" />
                    </div>
                    <span className="text-white">{item.feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-pink-500/20 border border-cyan-500/30 rounded-3xl p-12">
            <h2 className="text-4xl font-bold text-white mb-4">Ready to Transform Your Operations?</h2>
            <p className="text-slate-300 text-lg mb-8">
              Start your 14-day free trial today. No credit card required.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <button 
                onClick={() => navigate('/free-trial')}
                className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-400 hover:to-cyan-500 text-white px-8 py-4 rounded-xl font-semibold text-lg flex items-center gap-2 transition-all shadow-lg shadow-cyan-500/25"
              >
                Start Free Trial
                <ArrowRight className="w-5 h-5" />
              </button>
              <button 
                onClick={() => navigate('/login')}
                className="bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-xl font-semibold text-lg border border-white/20 transition-all"
              >
                Sign In
              </button>
            </div>
            
            <p className="text-slate-400 text-sm mt-8">
              "Every Patient Deserves to Arrive with Dignity"
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-slate-500 text-sm">
            © 2025 There You Are — NEMT Management Platform. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
